import React from 'react';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';

import { OnboardingReview, Dialog, DialogContent, DialogTitle, OnboardingStatusDots } from 'components';
import { EOnboardingStep } from 'enums';
import { useOnboardingContext } from './onboarding.context.hook';
import { DialogPrevNextClose } from './dialog-prev-next-close';
import { useAppSelector } from 'store/store.hook';
import { selectCurrentUserInfo } from 'store/slices/auth.slice';

interface OnboardingReviewDialogProps {
  visible?: boolean;
}

export const OnboardingReviewDialog = ({ visible = false }: OnboardingReviewDialogProps) => {
  const theme = useTheme();
  const down_sm = useMediaQuery(theme.breakpoints.down('sm'));
  const { setVisible, updateOnboarding } = useOnboardingContext();
  const { canCreateBuildings } = useAppSelector(selectCurrentUserInfo);

  const onClose = (event: any, reason: string) => {
    if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
      setVisible(false);
      document.body.removeAttribute('style');
    }
  };

  const onPrev = () => {
    updateOnboarding({ status: EOnboardingStep.ASSIGN_EMPLOYEE });
  };

  const next = () => {
    updateOnboarding({ status: EOnboardingStep.COMPLETED });
  };

  const title = "You're ready to go!";
  const subtitle = canCreateBuildings
    ? 'You have successfully finished setting up your account. Take a look at these next steps to add more buildings, create more zones, or order decals.'
    : 'You have successfully finished setting up your account. Take a look at these next steps to create more zones or order decals.';

  return (
    <Dialog
      onClose={onClose}
      open={visible}
      PaperProps={{ sx: { marginBottom: 12, ...(down_sm ? {} : { overflow: 'visible' }) } }}
    >
      <DialogTitle>
        <DialogPrevNextClose onPrev={onPrev} onClose={onClose} />

        <Box>
          <Typography variant="subtitle2" color="textSecondary">
            Step 6 of 6
          </Typography>
        </Box>

        <Box sx={{ marginTop: 2 }}>
          <Typography variant="h5">{title}</Typography>

          <Typography
            variant="body2"
            color="textSecondary"
            sx={{
              marginTop: 1,
            }}
          >
            {subtitle}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ paddingTop: 2, paddingBottom: 2 }}>
          <OnboardingReview next={next} />
        </Box>
      </DialogContent>

      <Box
        sx={{
          width: '100%',
          left: 0,
          display: 'flex',
          justifyContent: 'center',
          ...(down_sm ? { paddingBottom: 6 } : { position: 'absolute', bottom: -60 }),
        }}
      >
        <OnboardingStatusDots status={EOnboardingStep.ONBOARDING_REVIEW} />
      </Box>
    </Dialog>
  );
};
